import { NgService } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { ApiService } from "@app/services/api.service";
import { AuthService } from "@app/services/auth.service";
import { DialogService } from "@app/services/dialog.service";
import { KbService } from "@app/services/kb.service";
import { eWorkflowAction, IConfiguredProduct, IQuote, IQuoteProduct, IRootScope } from "@models";
import { StateService } from "@uirouter/angularjs";

export interface IAddProductArgs {
    idProduct?: number;
    qty?: number;
    configuredProduct?: IConfiguredProduct;
    addLocally?: boolean;
    silent?: boolean; // whether to suppress dialogs
    /**
     * When adding a product ends up creating a new quote, the product
     * can determine the quote currency.
     */
    currency?: string;
    /**
     * Only used in integrations that offer users the option of
     * selecting their own sku.
     */
    sku?: string;
}

@NgService({
    token: Token.QuoteService,
    dependencies: [
        Token.$RootScope,
        Token.$Q,
        Token.KbService,
        Token.$Http,
        Token.$State,
        Token.$CookieStore,
        Token.DialogService,
        Token.ApiService,
    ]
})
export class QuoteService {
   
    constructor(
        private $rootScope: IRootScope,
        private $q: ng.IQService,
        private kbService: KbService,
        private $http: ng.IHttpService,
        private $state: StateService,
        private $cookieStore: ng.cookies.ICookieStoreService,
        private dialogService: DialogService,
        private api: ApiService
    ) {
        this._initDeferred = $q.defer();
        this._initialized = false;

        let quoteIdFromCookie: any;
        try {
            quoteIdFromCookie = this.getActiveQuoteData();
        } catch (e) {
        }
        if (quoteIdFromCookie && quoteIdFromCookie.guidToken) {
            this._initDeferred.promise = $http.get<IQuote>("/api/quotes/guid/" + quoteIdFromCookie.guidToken, {
                ignoreSecurity: true,
                ignoreErrors: true
            }).then(r => {
                if (r.data.ownedBy == $rootScope.user.id ||
                    r.data.allowedActions.some(a => a.type == eWorkflowAction.modifyProducts)
                ) {
                    this.quote = r.data;
                } else {
                    // tslint:disable-next-line:no-console
                    console.log(
                        "Active Quote with ID " +
                        quoteIdFromCookie.guidToken +
                        " does not have proper add product permissions. Unsetting cookie."
                    );
                    this.$cookieStore.remove(QuoteService.COOKIE_NAME);
                }
                this._initialized = true;
            }, () => {
                // tslint:disable-next-line:no-console
                console.log("Active Quote with ID " +
                    quoteIdFromCookie.guidToken +
                    " does not exist. Unsetting cookie.");
                this.$cookieStore.remove(QuoteService.COOKIE_NAME);
                this._initialized = true;
            });

            this._initDeferred.resolve();
        } else {
            this._initialized = true;
        }
    }

    public static COOKIE_NAME: string = "activeQuote";

    private _initialized: boolean;
    public get isInitialized(): boolean {
        return this._initialized;
    }

    private _initDeferred: ng.IDeferred<any>;
    public get initPromise(): ng.IPromise<any> {
        return this._initDeferred.promise;
    }

    private _quote: IQuote;
    public get quote() {
        return this._quote;
    }
    public set quote(val: IQuote) {
        this._quote = val;

        // set the active quote in a cookie
        if (val) {
            this.storeCookie(val);
        } else {
            this.$cookieStore.remove(QuoteService.COOKIE_NAME);
        }
    }

    public storeCookie(quote: IQuote) {
        this.$cookieStore.put(QuoteService.COOKIE_NAME, JSON.stringify({
            idQuote: quote.id,
            guidToken: quote.guidToken
        }));
    }

    public getActiveQuoteData() {
        let cookie = this.$cookieStore.get(QuoteService.COOKIE_NAME);
        if (!cookie) return null;
        return JSON.parse(cookie);
    }

    public getLatestQuoteProduct(quote: IQuote): IQuoteProduct {
        let result: IQuoteProduct = null;

        quote.products.forEach(p => {
            if (!result) result = p;
            else {
                if (result.id < p.id) {
                    result = p;
                }
            }
        });

        return result;
    }

    public addProduct(args: IAddProductArgs): ng.IPromise<IQuoteProduct> {
        let deferred = this.$q.defer<IQuoteProduct>();
        // if there is no current quote, then we need to create one,
        // add the quoteProduct to it, and save on the server
        if (!this.quote) {
            this.quote = this.getNewQuote();
            if (args.currency) this.quote.currency = args.currency;
            
            // add just the id of the product to the quote products array
            this.quote.products.push({
                idProduct: args.idProduct,
                qty: args.qty,
                configuredProduct: args.configuredProduct,
                isConfigured: (args.configuredProduct !== null && args.configuredProduct !== undefined),
                sku: args.sku
            });

            // save it
            this.api.quotes.save(this.quote, this.$rootScope.contentTracker).then(r => {
                this.quote = r;
                
                deferred.resolve(this.getLatestQuoteProduct(r));
            });
        } else { // there is an active quote
            if (this.quote.idWorkflow &&
                !this.quote.allowedActions.some(a => a.type == eWorkflowAction.modifyProducts)
            ) {
                let confirm = () => {
                    this.quote = null;
                    this.addProduct(args).then((r) => {
                        deferred.resolve(this.getLatestQuoteProduct(r));
                    });
                };
                if (args.silent) {
                    confirm();
                } else {
                    this.dialogService.confirm("You cannot add products to quote '" +
                        this.quote.name +
                        "' at this time. Would you like to add it to a new quote?",
                        () => {
                            confirm();
                        });
                }
                    
            } else {
                // first we post to the server... it has it's own logic to figure
                // out duplicates and quantities
                let newQuoteProduct: IQuoteProduct = {
                    idProduct: args.idProduct,
                    qty: args.qty,
                    configuredProduct: args.configuredProduct,
                    isConfigured: (args.configuredProduct !== null && args.configuredProduct !== undefined),
                    sku: args.sku
                };

                this.api.quotes.insertQuoteProduct(
                    this.quote.id,
                    newQuoteProduct,
                    this.$rootScope.contentTracker
                ).then(r => {
                    this.quote = r;
                    deferred.resolve(this.getLatestQuoteProduct(r));
                });

                // now we figure it out locally to get an immediate update
                let existingProduct = this.quote.products.find(qp =>
                    !newQuoteProduct.isConfigured && qp.idProduct == newQuoteProduct.idProduct);

                if (args.addLocally) {
                    if (!existingProduct) {
                        this.quote.products.push(newQuoteProduct);
                    } else {
                        existingProduct.qty += args.qty;
                    }
                }
            }
        }
        return deferred.promise;
    }

    public canModifyProductsOfQuote(q: IQuote): boolean {
        return !q.idWorkflow || q.allowedActions.some(a => a.type == eWorkflowAction.modifyProducts);
    }

    public copyProductToActiveQuote(idQuoteProduct: number): ng.IPromise<any> {
        let deferred = this.$q.defer();

        if (this.canModifyProductsOfQuote(this.quote)) {
            this.api.quotes.copyQuoteProduct(
                this.quote.id,
                idQuoteProduct,
                this.$rootScope.contentTracker
            ).then(r => {
                this.quote = r;
                deferred.resolve();
            });
        } else {
            this.dialogService.confirm(
                "You cannot add products to quote '" +
                this.quote.name +
                "' at this time. Would you like to add it to a new quote?",
                () => {
                    this.quote = null;
                    this.copyProductToNewQuote(idQuoteProduct).then(() => {
                        deferred.resolve();
                    });
                });
        }
        return deferred.promise;
    }

    public copyProductToNewQuote(idQuoteProduct: number): ng.IPromise<any> {
        this.quote = this.getNewQuote();

        // save it
        return this.api.quotes.save(this.quote, this.$rootScope.contentTracker).then(r => {
            this.quote = r;
            return this.api.quotes.copyQuoteProduct(
                this.quote.id,
                idQuoteProduct,
                this.$rootScope.contentTracker
            ).then(r => {
                this.quote = r;
            });
        });
    }

    

    public goToQuote() {
        if (this.quote) {
            this.$state.go("kb.quoteEdit", { id: this.quote.id });
        } else {
            this.$state.go("kb.quoteNew");
        }
    }
    public getNewQuote(): IQuote {
        return {
            name: loc.newquote,
            createdBy: this.$rootScope.user.id,
            ownedBy: this.$rootScope.user.id,
            currency: this.$rootScope.companySettings.currency,
            quoteProducts: [],
            products: [],
            discountPercentage: 0
        } as IQuote;
    }
    public getQuoteById(id: number): ng.IPromise<IQuote> {
        let promise: ng.IPromise<any>;

        if (this.$rootScope.user.id != AuthService.ANONYMOUS_USER_ID) {
            promise = this.$http.get(
                "/api/quotes/" + id,
                { method: "GET", tracker: this.$rootScope.contentTracker } as ng.IRequestConfig);
        } else {
            let data = this.getActiveQuoteData();
            if (data && data.idQuote == id) {
                promise = this.$http.get(
                    "/api/quotes/guid/" + data.guidToken,
                    { method: "GET", tracker: this.$rootScope.contentTracker } as ng.IRequestConfig);
            } else {
                promise = this.$http.get(
                    "/api/quotes/" + id,
                    { method: "GET", tracker: this.$rootScope.contentTracker } as ng.IRequestConfig);
            }
        }
        return promise.then(data => {
            return (data ? data.data : null);
        });
    }

}
